import { useEffect, useState, useCallback, useRef, FormEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ShoppingCart,
  Package,
  Search,
  Plus,
  Check,
  Minus,
  X,
  AlertCircle,
  LogIn,
  UserPlus,
  CheckCircle,
  Send,
  ZoomIn,
  Sparkles,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import api from '../services/api'
import { useCart } from '../hooks/useCart'
import type { Product } from '../types'
import { formatCurrency, sanitizeHtml } from '../utils/helpers'
import logoEspaceClient from '../images/Logo-Espace-client.png'
import PasswordInput from '../components/PasswordInput'
import ProductDetailModal from '../components/ProductDetailModal'
import ShopAssistant from '../components/ShopAssistant'
import { useSeo } from '../hooks/useSeo'

interface SupplierBrand {
  erp_name: string
  logo: string | null
  website: string | null
  shop_enabled: boolean
  shop_show_prices: boolean
}

interface PublicCartItem {
  product: Product
  quantity: number
}

const PAGE_SIZE = 50

export default function PublicShop() {
  const { t } = useTranslation()
  const { siren, categoryLabel, productId } = useParams<{
    siren: string
    categoryLabel?: string
    productId?: string
  }>()
  const navigate = useNavigate()
  const { addToCart } = useCart()

  // Supplier branding
  const [supplier, setSupplier] = useState<SupplierBrand | null>(null)
  const [brandLoading, setBrandLoading] = useState(true)

  useSeo({
    title: supplier?.erp_name
      ? t('seo.shopTitleWithName', { name: supplier.erp_name })
      : t('seo.shopTitle'),
  })

  // Product catalog
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Category filters
  const [categories, setCategories] = useState<{ id: string; label: string }[]>([])
  const [selectedCategory, setSelectedCategory] = useState('')
  // Gate: on attend la résolution de la catégorie avant tout chargement produit
  // Si pas de categoryLabel dans l'URL, pas besoin d'attendre → true d'emblée
  const [categoriesReady, setCategoriesReady] = useState(!categoryLabel || categoryLabel === 'all')
  // True si le categoryLabel de l'URL ne correspond à aucune catégorie connue
  const [categoryNotFound, setCategoryNotFound] = useState(false)
  const [addedProducts, setAddedProducts] = useState<Set<string>>(new Set())
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set())
  const [detailProduct, setDetailProduct] = useState<Product | null>(null)
  // AI assistant filter: when non-null, the grid shows these products instead of the paginated list
  const [aiProducts, setAiProducts] = useState<Product[] | null>(null)
  const pageRef = useRef(1)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Public cart (separate from the authenticated CartContext cart)
  const [publicCart, setPublicCart] = useState<PublicCartItem[]>(() => {
    try {
      const key = `public_shop_cart_${siren ?? ''}`
      const saved = localStorage.getItem(key)
      if (saved) return JSON.parse(saved)
    } catch {
      /* ignore */
    }
    return []
  })

  const [showCart, setShowCart] = useState(false)
  const [showCheckout, setShowCheckout] = useState(false)

  // Checkout modal tab: 'login' | 'guest'
  const [checkoutTab, setCheckoutTab] = useState<'login' | 'guest'>('login')

  // Inline login state (inside checkout modal)
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)

  // Guest checkout form state
  const [guestForm, setGuestForm] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    company_name: '',
    siren: '',
    notes: '',
  })
  const [guestLoading, setGuestLoading] = useState(false)
  const [guestError, setGuestError] = useState('')
  const [guestSuccess, setGuestSuccess] = useState(false)

  // Persist public cart to localStorage
  useEffect(() => {
    const key = `public_shop_cart_${siren ?? ''}`
    localStorage.setItem(key, JSON.stringify(publicCart))
  }, [publicCart, siren])

  // Fetch supplier branding
  useEffect(() => {
    if (!siren) return
    setBrandLoading(true)
    api
      .getERPBrandBySiren(siren)
      .then((data) =>
        setSupplier({
          erp_name: data.erp_name,
          logo: data.logo,
          website: data.website,
          shop_enabled: data.shop_enabled ?? true,
          shop_show_prices: data.shop_show_prices ?? true,
        })
      )
      .catch(() => {
        /* supplier not found — show generic shop */
      })
      .finally(() => setBrandLoading(false))
  }, [siren])

  // Fetch product categories (optional — silently ignoré si non supporté par l'ERP)
  useEffect(() => {
    if (!siren) return
    // Réinitialise les flags à chaque changement de siren/categoryLabel
    setCategoryNotFound(false)
    if (categoryLabel && categoryLabel !== 'all') {
      setCategoriesReady(false)
    }
    api
      .getPublicShopCategories(siren)
      .then((cats) => {
        setCategories(cats)
        if (categoryLabel && categoryLabel !== 'all') {
          if (cats.length === 0) {
            // L'API catégories n'est pas supportée (retourne [])
            // → on passe le label brut à l'API produits, elle filtrera ce qu'elle peut
            setSelectedCategory(categoryLabel)
            setCategoryNotFound(false)
          } else {
            const match = cats.find(
              (c) =>
                c.label === categoryLabel ||
                c.label.toLowerCase() === categoryLabel.toLowerCase() ||
                c.id === categoryLabel
            )
            if (match) {
              setSelectedCategory(match.id)
              setCategoryNotFound(false)
            } else {
              // Catégorie vraiment introuvable dans une liste non vide → page vide
              setSelectedCategory('')
              setCategoryNotFound(true)
            }
          }
        } else {
          setSelectedCategory('')
          setCategoryNotFound(false)
        }
      })
      .catch(() => {
        // L'ERP ne supporte pas les catégories → on ne peut pas vérifier
        // On affiche tous les produits plutôt que de bloquer
        setSelectedCategory('')
        setCategoryNotFound(false)
      })
      .finally(() => setCategoriesReady(true))
  }, [siren, categoryLabel])

  // Debounce search input (300 ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Load the first page (reset list)
  const loadFirstPage = useCallback(
    async (search: string, category = '') => {
      if (!siren) return
      setLoading(true)
      setError(null)
      pageRef.current = 1
      try {
        const params: { page: number; limit: number; search?: string; category?: string } = {
          page: 1,
          limit: PAGE_SIZE,
        }
        if (search) params.search = search
        if (category) params.category = category
        const result = await api.getPublicShopProducts(siren, params)
        const items = result.data ?? []
        setProducts(items)
        setHasMore(result.pagination.page < result.pagination.pages)
      } catch {
        setError(t('shop.loadError'))
        setProducts([])
        setHasMore(false)
      } finally {
        setLoading(false)
      }
    },
    [siren, t]
  )

  // Load the next page and append
  const loadNextPage = useCallback(async () => {
    if (loadingMore || !hasMore || !siren) return
    setLoadingMore(true)
    const nextPage = pageRef.current + 1
    try {
      const params: { page: number; limit: number; search?: string; category?: string } = {
        page: nextPage,
        limit: PAGE_SIZE,
      }
      if (debouncedSearch) params.search = debouncedSearch
      if (selectedCategory) params.category = selectedCategory
      const result = await api.getPublicShopProducts(siren, params)
      const items = result.data ?? []
      setProducts((prev) => [...prev, ...items])
      pageRef.current = nextPage
      setHasMore(nextPage < result.pagination.pages)
    } catch {
      // silent
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, hasMore, debouncedSearch, selectedCategory, siren])

  // Charge un produit unique par son ID ou sa ref (pas de filtre catégorie)
  const loadProductById = useCallback(
    async (id: string) => {
      if (!siren) return
      setLoading(true)
      setError(null)
      pageRef.current = 1
      try {
        // On cherche avec le terme comme search pour réduire les résultats côté API
        const result = await api.getPublicShopProducts(siren, { page: 1, limit: 200, search: id })
        const items = result.data ?? []
        // Filtre strict côté client : id exact OU ref exacte
        const exact = items.filter((p) => String(p.id) === String(id) || p.ref === id)
        setProducts(exact)
        setHasMore(false)
        // Ouvre automatiquement le modal de détail si un produit correspond
        if (exact.length === 1) setDetailProduct(exact[0])
      } catch {
        setError(t('shop.loadError'))
        setProducts([])
        setHasMore(false)
      } finally {
        setLoading(false)
      }
    },
    [siren, t]
  )

  // Chargement produits : attend que la catégorie soit résolue (categoriesReady)
  // Si categoryNotFound → page vide immédiate, pas d'appel API
  // Si productId → affiche uniquement ce produit (ignore catégorie et recherche)
  // Sinon → catalogue filtré par catégorie + recherche
  useEffect(() => {
    if (!categoriesReady) return
    if (categoryNotFound) {
      // La catégorie n'existe pas → on affiche directement "aucun produit"
      setProducts([])
      setHasMore(false)
      setLoading(false)
      return
    }
    if (productId) {
      loadProductById(productId)
    } else {
      loadFirstPage(debouncedSearch, selectedCategory).catch(console.error)
    }
  }, [
    categoriesReady,
    categoryNotFound,
    productId,
    debouncedSearch,
    selectedCategory,
    loadFirstPage,
    loadProductById,
  ])

  // Infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) loadNextPage()
      },
      { threshold: 0.1 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
    // aiProducts: the sentinel unmounts while the AI filter is active — re-attach on clear
  }, [hasMore, loadingMore, loadNextPage, aiProducts])

  // Cart helpers
  const getQuantity = (productId: string) => quantities[productId] || 1

  const updateInputQuantity = (productId: string, value: number) => {
    if (value < 1) return
    setQuantities((prev) => ({ ...prev, [productId]: value }))
  }

  const addToPublicCart = (product: Product) => {
    const qty = getQuantity(product.id)
    setPublicCart((prev) => {
      const idx = prev.findIndex((i) => i.product.id === product.id)
      if (idx > -1) {
        const updated = [...prev]
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + qty }
        return updated
      }
      return [...prev, { product, quantity: qty }]
    })
    setAddedProducts((prev) => new Set(prev).add(product.id))
    setTimeout(() => {
      setAddedProducts((prev) => {
        const s = new Set(prev)
        s.delete(product.id)
        return s
      })
    }, 2000)
  }

  const removeFromPublicCart = (productId: string) => {
    setPublicCart((prev) => prev.filter((i) => i.product.id !== productId))
  }

  const updatePublicCartQty = (productId: string, qty: number) => {
    if (qty <= 0) {
      removeFromPublicCart(productId)
      return
    }
    setPublicCart((prev) =>
      prev.map((i) => (i.product.id === productId ? { ...i, quantity: qty } : i))
    )
  }

  const cartTotalHT = publicCart.reduce((sum, i) => sum + i.product.price * i.quantity, 0)
  const cartTotalTTC = publicCart.reduce((sum, i) => sum + i.product.price_ttc * i.quantity, 0)
  const cartItemCount = publicCart.reduce((sum, i) => sum + i.quantity, 0)

  // After successful login: transfer public cart to CartContext and navigate to /cart
  const handleLogin = async (e: FormEvent) => {
    e.preventDefault()
    setLoginError('')
    setLoginLoading(true)
    try {
      await api.login(loginEmail, loginPassword)
      // Transfer items
      for (const item of publicCart) {
        addToCart(item.product, item.quantity)
      }
      // Clear public cart
      const key = `public_shop_cart_${siren ?? ''}`
      localStorage.removeItem(key)
      setPublicCart([])
      navigate('/cart')
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setLoginError(apiErr.response?.data?.error || t('shop.loginError'))
    } finally {
      setLoginLoading(false)
    }
  }

  // Submit guest order (no account needed)
  const handleGuestSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!siren) return
    setGuestError('')
    setGuestLoading(true)
    try {
      const cartPayload = publicCart.map((item) => ({
        product_id: item.product.id,
        ref: item.product.ref,
        label: item.product.label,
        quantity: item.quantity,
        unit_price: item.product.price,
        price_ttc: item.product.price_ttc * item.quantity,
      }))
      await api.submitPublicShopOrder(siren, { ...guestForm, cart: cartPayload })
      // Clear public cart on success
      const key = `public_shop_cart_${siren ?? ''}`
      localStorage.removeItem(key)
      setPublicCart([])
      setGuestSuccess(true)
    } catch {
      setGuestError(t('shop.guestError'))
    } finally {
      setGuestLoading(false)
    }
  }

  const imageUrl = (productId: string) => (siren ? api.getPublicShopImageUrl(siren, productId) : '')

  if (brandLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Shop disabled for this supplier
  if (supplier && !supplier.shop_enabled) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 max-w-md text-center">
          {supplier.logo ? (
            <img
              src={supplier.logo}
              alt={supplier.erp_name}
              className="h-12 mx-auto mb-6 object-contain"
            />
          ) : (
            <img src={logoEspaceClient} alt="Boutique" className="h-10 mx-auto mb-6" />
          )}
          <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {t('shop.shopDisabledTitle')}
          </h2>
          <p className="text-gray-500 text-sm">{t('shop.shopDisabledMessage')}</p>
          {supplier.website && (
            <a
              href={supplier.website}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-primary-600 hover:underline text-sm font-medium"
            >
              {supplier.erp_name}
            </a>
          )}
        </div>
      </div>
    )
  }

  const showPrices = supplier?.shop_show_prices !== false

  // Products shown in the grid: AI filter result takes precedence over the paginated list
  const displayedProducts = aiProducts ?? products

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
          {/* Branding */}
          <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
            {supplier?.logo ? (
              supplier.website ? (
                <a
                  href={supplier.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={supplier.website}
                >
                  <img
                    src={supplier.logo}
                    alt={supplier.erp_name}
                    className="h-10 object-contain flex-shrink-0 hover:opacity-80 transition-opacity"
                  />
                </a>
              ) : (
                <img
                  src={supplier.logo}
                  alt={supplier.erp_name}
                  className="h-10 object-contain flex-shrink-0"
                />
              )
            ) : supplier ? (
              supplier.website ? (
                <a
                  href={supplier.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-bold text-primary-700 truncate hover:underline"
                >
                  {supplier.erp_name}
                </a>
              ) : (
                <span className="text-lg font-bold text-primary-700 truncate">
                  {supplier.erp_name}
                </span>
              )
            ) : (
              <img src={logoEspaceClient} alt="Boutique" className="h-10" />
            )}
            {supplier && (
              <div className="hidden sm:block border-l border-gray-200 pl-3">
                <img src={logoEspaceClient} alt="Espace Client" className="h-5 opacity-40" />
              </div>
            )}
          </div>

          {/* Center title */}
          <div className="flex-1 text-center">
            <span className="text-lg font-semibold text-gray-700">Boutique</span>
          </div>

          {/* Language selector + cart */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => i18n.changeLanguage('fr')}
                className={`px-2 py-1 text-xs font-semibold rounded-md transition-all ${
                  i18n.language === 'fr' || i18n.language?.startsWith('fr')
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                FR
              </button>
              <button
                onClick={() => i18n.changeLanguage('en')}
                className={`px-2 py-1 text-xs font-semibold rounded-md transition-all ${
                  i18n.language === 'en' || i18n.language?.startsWith('en')
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                EN
              </button>
            </div>
            <button
              onClick={() => setShowCart(true)}
              className="relative flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="hidden sm:inline">{t('shop.cart')}</span>
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('shop.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Category filters */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate(`/shop/${siren}/all`)}
              className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                selectedCategory === ''
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400 hover:text-primary-600'
              }`}
            >
              {t('shop.categoryAll')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => navigate(`/shop/${siren}/${encodeURIComponent(cat.label)}`)}
                className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400 hover:text-primary-600'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}

        {/* AI assistant active filter banner */}
        {aiProducts !== null && (
          <div className="flex items-center justify-between bg-primary-50 border border-primary-200 rounded-xl px-4 py-3">
            <span className="flex items-center gap-2 text-sm text-primary-800">
              <Sparkles className="w-4 h-4 flex-shrink-0" />
              {t('shopAssistant.activeFilter', { count: aiProducts.length })}
            </span>
            <button
              onClick={() => setAiProducts(null)}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline flex-shrink-0"
            >
              {t('shopAssistant.showAll')}
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-800 mb-4">{error}</p>
            <button
              onClick={() => loadFirstPage(debouncedSearch)}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('common.retry')}
            </button>
          </div>
        )}

        {/* Product grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedProducts.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500">{t('shop.notFound')}</p>
              </div>
            ) : (
              displayedProducts.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col"
                >
                  {/* Image — click to open detail modal */}
                  <div
                    className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden rounded-t-xl cursor-pointer group/img"
                    onClick={() => setDetailProduct(product)}
                  >
                    {!brokenImages.has(product.id) ? (
                      <img
                        src={imageUrl(product.id)}
                        alt={product.label}
                        className="w-full h-full object-contain p-2"
                        onError={() => setBrokenImages((prev) => new Set(prev).add(product.id))}
                      />
                    ) : (
                      <Package className="w-16 h-16 text-gray-400" />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors flex items-center justify-center">
                      <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover/img:opacity-100 transition-opacity drop-shadow" />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-5 flex flex-col flex-grow">
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{product.label}</h3>
                        <p className="text-sm text-gray-500">
                          {t('catalog.ref', { ref: product.ref })}
                        </p>
                      </div>
                      <span
                        className={`ml-2 flex-shrink-0 px-2 py-1 text-xs font-medium rounded-full ${
                          product.type === 0
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {product.type === 0 ? t('catalog.typeProduct') : t('catalog.typeService')}
                      </span>
                    </div>

                    {product.description && (
                      <div className="relative group/desc mb-4">
                        {/* Truncated description */}
                        {product.description.includes('<') ? (
                          <div
                            className="text-sm text-gray-600 line-clamp-3 product-description cursor-default"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }}
                          />
                        ) : (
                          <p className="text-sm text-gray-600 line-clamp-2 cursor-default">
                            {product.description}
                          </p>
                        )}
                        {/* Full description tooltip on hover */}
                        <div className="absolute left-0 top-full mt-1 z-50 w-72 max-w-full opacity-0 invisible group-hover/desc:opacity-100 group-hover/desc:visible transition-opacity duration-150">
                          <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-3 max-h-52 overflow-y-auto text-sm text-gray-700 leading-relaxed">
                            {product.description.includes('<') ? (
                              <div
                                className="product-description"
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeHtml(product.description),
                                }}
                              />
                            ) : (
                              product.description
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {showPrices && product.price_ttc > 0 && (
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="text-xs text-gray-400">{t('catalog.priceExclTax')}</p>
                          <p className="text-lg font-bold text-gray-900">
                            {formatCurrency(product.price)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">{t('catalog.priceInclTax')}</p>
                          <p className="text-lg font-bold text-primary-600">
                            {formatCurrency(product.price_ttc)}
                          </p>
                        </div>
                      </div>
                    )}

                    {product.stock !== null && product.stock !== undefined && (
                      <p
                        className={`text-sm mb-4 ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}
                      >
                        {product.stock > 0
                          ? t('catalog.inStock', { count: product.stock })
                          : t('catalog.outOfStock')}
                      </p>
                    )}

                    {/* Quantity selector */}
                    <div className="flex items-center justify-center space-x-2 mb-4 mt-auto">
                      <button
                        onClick={() => updateInputQuantity(product.id, getQuantity(product.id) - 1)}
                        className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        disabled={getQuantity(product.id) <= 1}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={getQuantity(product.id)}
                        onChange={(e) => {
                          const v = parseInt(e.target.value)
                          if (!isNaN(v) && v >= 1) updateInputQuantity(product.id, v)
                        }}
                        className="w-20 text-center px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                      />
                      <button
                        onClick={() => updateInputQuantity(product.id, getQuantity(product.id) + 1)}
                        className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    <button
                      onClick={() => addToPublicCart(product)}
                      disabled={product.stock === 0}
                      className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                        addedProducts.has(product.id)
                          ? 'bg-green-600 text-white'
                          : product.stock === 0
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-primary-600 text-white hover:bg-primary-700'
                      }`}
                    >
                      {addedProducts.has(product.id) ? (
                        <>
                          <Check className="w-5 h-5" />
                          <span>{t('catalog.addedToCart')}</span>
                        </>
                      ) : (
                        <>
                          <ShoppingCart className="w-5 h-5" />
                          <span>{t('catalog.addToCart')}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Infinite scroll sentinel — disabled while the AI filter is active */}
        {aiProducts === null && (
          <div ref={sentinelRef} className="flex justify-center py-6">
            {loadingMore && (
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                {t('catalog.loadingMore')}
              </div>
            )}
            {!hasMore && products.length > 0 && !loadingMore && (
              <p className="text-sm text-gray-400">{t('catalog.noMoreProducts')}</p>
            )}
          </div>
        )}
      </div>

      {/* AI shop assistant (chatbot + voice bot) */}
      {siren && (
        <ShopAssistant
          onSearch={async (query) => {
            const result = await api.aiSearchPublicShop(siren, query)
            setAiProducts(result.data)
            return { total: result.total, keywords: result.keywords }
          }}
          onClear={() => setAiProducts(null)}
        />
      )}

      {/* Footer */}
      <footer className="py-6 border-t border-gray-200 text-center text-xs text-gray-400">
        <p>
          {t('landing.footerService')}{' '}
          <a
            href="https://www.iseffi.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:underline"
          >
            www.iseffi.com
          </a>
        </p>
      </footer>

      {/* Product detail modal */}
      {detailProduct &&
        (() => {
          const p = detailProduct
          return (
            <ProductDetailModal
              product={p}
              onClose={() => setDetailProduct(null)}
              imageNode={
                !brokenImages.has(p.id) ? (
                  <img
                    src={imageUrl(p.id)}
                    alt={p.label}
                    className="w-full h-full object-contain p-4"
                    onError={() => setBrokenImages((prev) => new Set(prev).add(p.id))}
                  />
                ) : (
                  <Package className="w-24 h-24 text-gray-400" />
                )
              }
              quantity={getQuantity(p.id)}
              onQuantityChange={(qty) => updateInputQuantity(p.id, qty)}
              onAddToCart={() => {
                addToPublicCart(p)
              }}
              isAdded={addedProducts.has(p.id)}
            />
          )
        })()}

      {/* Cart panel (slide-over) */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCart(false)} />
          <div className="relative w-full max-w-md bg-white h-full flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{t('shop.cartTitle')}</h2>
              <button
                onClick={() => setShowCart(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {publicCart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">{t('shop.cartEmpty')}</p>
                </div>
              ) : (
                publicCart.map((item) => (
                  <div
                    key={item.product.id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {item.product.label}
                      </p>
                      <p className="text-xs text-gray-500">{item.product.ref}</p>
                      {showPrices && (
                        <p className="text-sm font-semibold text-primary-600 mt-0.5">
                          {formatCurrency(item.product.price_ttc * item.quantity)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => updatePublicCartQty(item.product.id, item.quantity - 1)}
                        className="p-1 border border-gray-300 rounded hover:bg-white transition-colors"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm w-6 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updatePublicCartQty(item.product.id, item.quantity + 1)}
                        className="p-1 border border-gray-300 rounded hover:bg-white transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeFromPublicCart(item.product.id)}
                        className="p-1 text-red-400 hover:text-red-600 ml-1"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {publicCart.length > 0 && (
              <div className="p-5 border-t border-gray-200 space-y-3">
                {showPrices && (
                  <>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{t('cart.totalExclTax')}</span>
                      <span className="font-medium">{formatCurrency(cartTotalHT)}</span>
                    </div>
                    <div className="flex justify-between text-base font-bold text-gray-900">
                      <span>{t('cart.totalInclTax')}</span>
                      <span className="text-primary-600">{formatCurrency(cartTotalTTC)}</span>
                    </div>
                  </>
                )}
                <button
                  onClick={() => {
                    setShowCart(false)
                    setShowCheckout(true)
                  }}
                  className="w-full py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                >
                  {t('shop.checkout')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checkout modal — two tabs: Sign in | Order without account */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">{t('shop.checkoutTitle')}</h2>
              <button
                onClick={() => {
                  setShowCheckout(false)
                  setGuestSuccess(false)
                  setGuestError('')
                  setLoginError('')
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Success state */}
            {guestSuccess ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-4">
                <CheckCircle className="w-16 h-16 text-green-500" />
                <h3 className="text-xl font-bold text-gray-900">{t('shop.guestSuccessTitle')}</h3>
                <p className="text-sm text-gray-600">{t('shop.guestSuccessDetail')}</p>
                <button
                  onClick={() => {
                    setShowCheckout(false)
                    setGuestSuccess(false)
                  }}
                  className="mt-4 px-6 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  {t('common.close')}
                </button>
              </div>
            ) : (
              <>
                {/* Tabs */}
                <div className="flex border-b border-gray-200 flex-shrink-0">
                  <button
                    onClick={() => setCheckoutTab('login')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors border-b-2 ${
                      checkoutTab === 'login'
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <LogIn className="w-4 h-4" />
                    {t('shop.tabLogin')}
                  </button>
                  <button
                    onClick={() => setCheckoutTab('guest')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors border-b-2 ${
                      checkoutTab === 'guest'
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <UserPlus className="w-4 h-4" />
                    {t('shop.tabGuest')}
                  </button>
                </div>

                {/* Tab content */}
                <div className="overflow-y-auto flex-1">
                  {/* Login tab */}
                  {checkoutTab === 'login' && (
                    <div className="p-6 space-y-4">
                      <p className="text-sm text-gray-600">{t('shop.checkoutInfo')}</p>

                      {loginError && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-red-700">{loginError}</p>
                        </div>
                      )}

                      <form onSubmit={handleLogin} className="space-y-3">
                        <input
                          type="email"
                          placeholder={t('login.emailPlaceholder')}
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                        />
                        <PasswordInput
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          required
                          placeholder="••••••••"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                        />
                        <button
                          type="submit"
                          disabled={loginLoading}
                          className="w-full py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                        >
                          {loginLoading ? t('login.loading') : t('shop.loginAndOrder')}
                        </button>
                      </form>

                      <p className="text-xs text-center text-gray-500">
                        {t('shop.noAccount')}{' '}
                        <a
                          href={`${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/register`}
                          className="text-primary-600 hover:underline font-medium"
                        >
                          {t('shop.registerLink')}
                        </a>
                      </p>
                    </div>
                  )}

                  {/* Guest tab */}
                  {checkoutTab === 'guest' && (
                    <div className="p-6">
                      {guestError && (
                        <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg">
                          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-red-700">{guestError}</p>
                        </div>
                      )}

                      <form onSubmit={handleGuestSubmit} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {t('shop.guestFirstname')} *
                            </label>
                            <input
                              type="text"
                              value={guestForm.firstname}
                              onChange={(e) =>
                                setGuestForm((f) => ({ ...f, firstname: e.target.value }))
                              }
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {t('shop.guestLastname')} *
                            </label>
                            <input
                              type="text"
                              value={guestForm.lastname}
                              onChange={(e) =>
                                setGuestForm((f) => ({ ...f, lastname: e.target.value }))
                              }
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('shop.guestEmail')} *
                          </label>
                          <input
                            type="email"
                            value={guestForm.email}
                            onChange={(e) => setGuestForm((f) => ({ ...f, email: e.target.value }))}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {t('shop.guestPhone')}
                            </label>
                            <input
                              type="tel"
                              value={guestForm.phone}
                              onChange={(e) =>
                                setGuestForm((f) => ({ ...f, phone: e.target.value }))
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {t('shop.guestSiren')}
                            </label>
                            <input
                              type="text"
                              value={guestForm.siren}
                              onChange={(e) =>
                                setGuestForm((f) => ({ ...f, siren: e.target.value }))
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('shop.guestCompany')}
                          </label>
                          <input
                            type="text"
                            value={guestForm.company_name}
                            onChange={(e) =>
                              setGuestForm((f) => ({ ...f, company_name: e.target.value }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t('shop.guestNotes')}
                          </label>
                          <textarea
                            value={guestForm.notes}
                            onChange={(e) => setGuestForm((f) => ({ ...f, notes: e.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm resize-none"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={guestLoading}
                          className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                        >
                          <Send className="w-4 h-4" />
                          {guestLoading ? t('shop.guestSending') : t('shop.guestSubmit')}
                        </button>
                      </form>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
